import { createCrmApi, CrmConnection } from 'services/crm/api'
// Form Spam presentation model. The production provider uses Lead Forms APIs;
// the isolated design route retains its original preview data. `installed` is the GLOBAL install gate: the form protection code
// rides with the site tracking code, so one flag covers every form. Everything
// else (widget, email validation, captcha) is per form.
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react'
import type { LeadFormDTO, LeadFormEmailFlagDTO } from 'api-models'
import { LeadFormsApi, Setup, FormDraft } from '../lead-forms/api'
import { usePrefersReducedMotion } from '@clickguard/design-system'

/**
 * R70: a keyed cross-fade, with a real exit.
 *
 * `.form-spam-panel` fades a keyed panel IN and nothing fades the outgoing one
 * OUT, so a key change drops the old panel in the same frame and the reader
 * sees empty space while the new one arrives. That is a hard cut.
 *
 * This lags the PAINTED key by one exit, the way the drawer already lags its
 * tabs (`form-detail-drawer.tsx`, `paintedTab` + `leaving`): the caller marks
 * the outgoing node `.form-spam-leave`, and only when that finishes does the
 * new key mount and play `.form-spam-panel`.
 *
 * Reduced motion swaps at once. `.form-spam-leave` is `animation: none` there,
 * so a lag would be 150ms of nothing.
 *
 * DS GAP TO REPORT: the DS ships `useEnterExit` for a BOOLEAN and nothing for a
 * keyed swap, so every lane hand-rolls this. Do not patch the DS from here.
 */
export const useSwapKey = <K extends string>(key: K, ms = 150) => {
	const reduced = usePrefersReducedMotion()
	const [painted, setPainted] = useState<K>(key)
	const [leaving, setLeaving] = useState(false)
	const timer = useRef<number>()

	// Render phase, NOT an effect. An effect would let one frame paint the new
	// contents under the old key before the exit starts, and that flicker reads
	// worse than the hard cut it replaces. React sanctions this shape for state
	// that has to follow a prop.
	if (painted !== key && !leaving) {
		if (reduced) setPainted(key)
		else setLeaving(true)
	}

	// The exit runs, then the new key mounts and plays its own enter. `key` is
	// a dep, so a second change mid-exit lands on the NEWEST key.
	useEffect(() => {
		if (!leaving) return
		window.clearTimeout(timer.current)
		timer.current = window.setTimeout(() => {
			setPainted(key)
			setLeaving(false)
		}, ms)
	}, [leaving, key, ms])

	useEffect(() => () => window.clearTimeout(timer.current), [])

	return { painted, leaving }
}

/**
 * R114: keeps a swapping panel's box on the height its contents really have.
 *
 * `useSwapKey` fades the panel over, but a facet that drops rows also makes the
 * panel SHORTER, and that height moved in one frame at the commit. The card
 * snapped and every pixel under it jumped. His words: "swapping between this
 * moves the whole page".
 *
 * The first build read the new height in a layout effect and eased to it. That
 * measured 20px: the table lays its rows out one frame AFTER the commit, so the
 * box shrank to nothing and then popped back. Measured 2026-08-26.
 *
 * So this predicts nothing. A `ResizeObserver` reports the height the contents
 * really took, and the outer box eases to it in CSS. That is right for a facet
 * swap, and it is right for a row toggle or a page turn too.
 *
 * The observer hangs off a CALLBACK ref, not a layout effect. The drawer mounts
 * long before its feed does, so a one-shot effect ran against a null node and
 * the height was never written at all. React hands a callback ref every live
 * node, however late it arrives, and null on the way out.
 *
 * A measure of 0 means "not laid out yet", never a real height. Round 42 locked
 * the drawer feed at `height: 0` by believing one.
 *
 * Reduced motion keeps the observer. The CSS drops the transition, so the box
 * still fits its contents and the move is instant.
 *
 * R300 (Esteban, 11 Sep): "weird glitch when switching between tabs". The hook
 * used to read once in the commit and then one frame after every resize. On a
 * fresh table the commit read is the 76px shell, the rows land a frame or two
 * later, and the 240ms transition played 76px to 791px: the table unrolled
 * from a stub. Now nothing is written until the contents hold still
 * (`settleHeight`), so a fresh box opens at its real height, and every later
 * change waits for two frames that agree before the box eases to it.
 */
export const useFlowHeight = () => {
	const [height, setHeight] = useState<number>()
	const watcher = useRef<ResizeObserver | null>(null)
	const cancel = useRef<(() => void) | null>(null)

	const inner = useCallback((el: HTMLDivElement | null) => {
		watcher.current?.disconnect()
		watcher.current = null
		cancel.current?.()
		cancel.current = null
		if (!el || typeof ResizeObserver === 'undefined') return

		// No read in the commit: the box stays `auto` until the first settled
		// height, so `auto` never animates and the rows paint at full size. A
		// resize that lands while a read is already in flight is picked up by
		// that read, which polls every frame until the height holds.
		const follow = () => {
			if (cancel.current) return
			cancel.current = settleHeight(el, (settled) => {
				cancel.current = null
				setHeight(settled || undefined)
			})
		}
		watcher.current = new ResizeObserver(follow)
		watcher.current.observe(el)
	}, [])

	return { inner, height }
}

/**
 * R300: reads an element's height once it holds still.
 *
 * A table paints its shell in the commit and its rows one frame later, and a
 * slower machine takes two or three. A read on the first frame sees the shell,
 * the box eases to it, the rows land, and the box eases again. So a height
 * counts only when `stable` consecutive frames agree on it. Two was not
 * enough: measured 2026-09-11, the Submissions shell held for exactly two
 * frames before its rows painted. The cap keeps a box whose contents never
 * settle from waiting forever. Returns the cancel.
 */
const settleHeight = (
	el: HTMLElement,
	done: (height: number) => void,
	{ stable = 3, cap = 15 } = {}
) => {
	let last = -1
	let run = 0
	let frames = 0
	let handle = 0
	const tick = () => {
		const height = el.getBoundingClientRect().height
		frames += 1
		run = height > 0 && height === last ? run + 1 : 1
		last = height
		if (run >= stable || frames >= cap) {
			handle = 0
			done(height)
			return
		}
		handle = window.requestAnimationFrame(tick)
	}
	handle = window.requestAnimationFrame(tick)
	return () => window.cancelAnimationFrame(handle)
}

/**
 * R300 (Esteban, 11 Sep): "weird glitch when switching between tabs".
 *
 * `useSwapKey` crossfades the Forms and Submissions panels, but the card had no
 * height of its own between them. At the swap the old panel left and the new
 * one mounted as a 76px shell, so the card dropped from 564px to 194px in one
 * frame, the page scroller lost that height and threw the scroll to the top,
 * and then the new table eased up from the stub. Measured 2026-09-11.
 *
 * This pins the slot's box on the outgoing height for the exit, waits for the
 * incoming contents to hold still (`settleHeight`), eases the box to them, and
 * then lets go: `height: auto` again, so the panels' own frames drive their
 * in-table swaps without a second box easing over them. The slot is passive
 * except across a keyed swap. Reduced motion never sets `leaving`, so the slot
 * never pins and the swap is the instant one that mode asks for.
 *
 * `frame` is the box (inline height, overflow hidden). `inner` is the content
 * it measures.
 */
export const useSwapHold = (painted: string, leaving: boolean) => {
	const frame = useRef<HTMLDivElement>(null)
	const inner = useRef<HTMLDivElement>(null)
	const [height, setHeight] = useState<number>()
	const pinned = useRef(false)

	// Pin on the first frame of the exit. The leaving panel keeps its layout
	// height (the exit is opacity and transform), so this reads the true one.
	useEffect(() => {
		if (!leaving || !frame.current) return
		const current = frame.current.getBoundingClientRect().height
		if (!current) return
		pinned.current = true
		setHeight(current)
	}, [leaving])

	// After the swap the box is still pinned. Watch the new contents until they
	// settle, ease to them, and let go 260ms after the last write (the
	// transition is 240ms in `.form-spam-swap-frame`). The watch stays on for
	// that window: a table whose rows land late re-targets the ease instead of
	// jumping at the release. The release writes `auto` on the same pixel
	// value, so nothing moves.
	useEffect(() => {
		if (!pinned.current || leaving) return
		pinned.current = false
		const el = inner.current
		if (!el || typeof ResizeObserver === 'undefined') {
			setHeight(undefined)
			return
		}
		let cancel: (() => void) | null = null
		let release = 0
		const watcher = new ResizeObserver(() => {
			if (cancel) return
			cancel = settleHeight(el, (settled) => {
				cancel = null
				setHeight(settled || undefined)
				window.clearTimeout(release)
				release = window.setTimeout(() => {
					watcher.disconnect()
					setHeight(undefined)
				}, 260)
			})
		})
		watcher.observe(el)
		return () => {
			watcher.disconnect()
			cancel?.()
			window.clearTimeout(release)
		}
	}, [painted, leaving])

	return { frame, inner, height }
}

export type FormStatus = 'live' | 'waiting' | 'paused'

/** What the product does when a check fails. */
export type FailAction = 'block' | 'flag'

/** When the captcha is raised. Captcha is POLICY, not appearance. */
export type CaptchaTrigger = 'suspected' | 'high-only' | 'always'

/**
 * R278 (Esteban, 4 Sep, item 5): the Normal / Strict row under the two bot
 * switches. Its OWN field. The drawer derives the switches from `botAction` +
 * `captchaTrigger` (R262), so a value written into `captchaTrigger` would flip
 * a switch. Normal acts at a bot probability of 90% or more; Strict at 80%.
 * R309 (Gustavo pin #81): the SAME two steps serve the CAPTCHA switch's own
 * field, `captchaSensitivity`, at 60% / 40%. Each switch row folds its own
 * Normal / Strict in while the switch is on.
 */
export type BotSensitivity = 'normal' | 'strict'

export const BOT_SENSITIVITIES: { key: BotSensitivity; label: string }[] = [
	{ key: 'normal', label: 'Normal' },
	{ key: 'strict', label: 'Strict' }
]

/**
 * R323 (Gustavo, 13 Sep, item 1): the Threshold row on the Protect my ads
 * pane. "Block when the [first / second / third / fourth] bad submission
 * happens." The default is the second one: a single bad submission can come
 * from a real visitor, two is spam.
 *
 * R333 (Gustavo, 14 Sep, Cherry pin #101): three steps, not four. The note
 * under the tabs now reads "Block after the [first / second / third] bad
 * submission from the same visitor."
 */
export type BlockThreshold = 1 | 2 | 3

export const BLOCK_THRESHOLDS: { key: BlockThreshold; label: string }[] = [
	{ key: 1, label: 'First' },
	{ key: 2, label: 'Second' },
	{ key: 3, label: 'Third' }
]

export type CaptchaProvider = 'clickguard' | 'turnstile' | 'recaptcha'

/**
 * How hard the bot check acts on the person at the form.
 *
 * The level names its ACTION, never a strength word. Ralph, product sync
 * 2026-08-20: we cannot make the decision for the account without giving them
 * control, or the liability is ours. "If I didn't necessarily consent, hey, I'm
 * choosing this aggressive level, this is the action that I want taken, and you
 * did that completely on your own, we've got a whole different conversation
 * now." Gustavo's counter is why the label is not "Aggressive": the user does
 * not know what aggressive means. So each level says what happens to the
 * visitor, and what it costs when the score is wrong.
 */
export type BotLevel = 'monitor' | 'challenge' | 'block'

/** The words of one authorization ask. */
export interface AuthorizeCopy {
	title: string
	body: string
	confirm: string
}

/**
 * R49: the degree no longer opens a confirm dialog. The user agrees in place,
 * on a checkbox under the picker, and the wizard holds Next until they check
 * it. The words are here so both hosts, the wizard and the drawer, read one
 * source.
 */
export interface BotConsent {
	/** What the user accepts. The row writes the policy sentence after it. */
	accept: string
	/** The name of the policy the row links to. */
	policy: string
	/**
	 * Prototype: ClickGuard has no bot policy page yet, so the link points at
	 * the live Terms of Service. Repoint it when the page exists.
	 */
	href: string
}

export interface BotLevelDef {
	key: BotLevel
	label: string
	desc: string
	/** `null` means the level touches no visitor, so it needs no agreement. */
	consent: BotConsent | null
}

/** One policy for both degrees, so the link never drifts between them. */
const BOT_POLICY = {
	policy: 'Bot Protection Policy',
	href: 'https://www.clickguard.com/terms-of-service'
}

/**
 * Monitor stands first and it is the default of a new form: Ralph asked for
 * the account to see what we catch before it lets us act. The other two ask
 * for the user's word first, every time they go live.
 */
export const BOT_LEVELS: BotLevelDef[] = [
	{
		key: 'monitor',
		label: 'Monitor only',
		desc: 'We score every submission and flag the likely bots. Nobody is stopped, and your form does not change.',
		consent: null
	},
	{
		key: 'challenge',
		label: 'Show a CAPTCHA',
		desc: 'A likely bot must pass a CAPTCHA before the form goes through. A real person the score gets wrong must pass it too.',
		// The checkbox names the COST, never the action. The radio above it
		// already says what happens; repeating that would waste the one line the
		// user actually reads before they agree.
		consent: {
			...BOT_POLICY,
			accept:
				'I accept that a real person the score gets wrong must pass the CAPTCHA too'
		}
	},
	{
		key: 'block',
		label: 'Block the submission',
		// R69: the degree names the ACTION only. The target row under the radios
		// now names WHO the action reaches, so this line must not name a tier.
		desc: 'The submission stops at the form and never reaches you. A real person the score gets wrong loses the lead.',
		consent: {
			...BOT_POLICY,
			accept:
				'I accept that a real person the score gets wrong loses the lead'
		}
	}
]

/** Where the widget sits inside the host form. Widget is APPEARANCE. */
export type WidgetPlacement = 'above-submit' | 'below-submit' | 'top'

export type WidgetTheme = 'auto' | 'light' | 'dark'

export interface EmailChecks {
	syntax: boolean
	domain: boolean
	disposable: boolean
	roleBased: boolean
}

/**
 * The 8-rule email screen the link flow shows. The store keeps the full rule
 * map so the drawer can show the exact same list; the coarser `EmailChecks`
 * stays derived from it for the older read paths.
 */
export interface EmailRule {
	key: string
	label: string
	desc: string
	default: boolean
}

/**
 * R213 (Esteban, 1 Sep): "By default these are on, the others off". Only the
 * two rules that name a certainly bad address start on. Invalid says the inbox
 * does not exist, and Spam traps says the address is on a known abuse list, so
 * a flag on either one cannot cost a real lead.
 *
 * The other four describe an address we cannot confirm, not a bad one. A
 * catch-all domain, an alias, a role inbox and a silent mail server all belong
 * to real customers every day, so a new form does not flag them until the
 * account asks for it.
 */
export const EMAIL_RULES: EmailRule[] = [
	{ key: 'invalid', label: 'Invalid', desc: 'Address does not exist or the domain cannot receive mail', default: true },
	{ key: 'spamTraps', label: 'Spam traps', desc: 'On a known spam/abuse list: a bad domain or a reported address', default: true },
	{ key: 'doNotMail', label: 'Do not mail', desc: 'Disposable or role inboxes (info@, support@, throwaways)', default: false },
	{ key: 'catchAll', label: 'Catch-all', desc: 'Domain accepts any address, so the real inbox cannot be confirmed', default: false },
	{ key: 'alias', label: 'Alias', desc: 'Forwarding address, so the real inbox is elsewhere (iCloud Hide My Email, SimpleLogin)', default: false },
	{ key: 'unknown', label: 'Unknown', desc: 'Mail server does not answer, so the inbox cannot be confirmed', default: false }
]

export const emailDefaults = (): Record<string, boolean> =>
	Object.fromEntries(EMAIL_RULES.map((rule) => [rule.key, rule.default]))

/**
 * R292 (Esteban, 10 Sep): "you shouldn't be able to turn all toggles off,
 * without the actual general toggle for the feature to go off - means at
 * least one of the toggles below should be on, if that last one is turned
 * off by the user, the whole thing should turn off with it".
 *
 * The master and the rules are one value, and every write goes through the
 * two setters below, so a form never holds the switch on over an empty list.
 * Both the wizard and the drawer call them through `EmailPane`.
 */
export interface EmailSetting {
	on: boolean
	rules: Record<string, boolean>
}

export const anyEmailRule = (rules: Record<string, boolean>): boolean =>
	EMAIL_RULES.some((rule) => rules[rule.key])

/** One rule flips. The last rule to go off takes the master off with it. */
export const setEmailRule = (current: EmailSetting, key: string, next: boolean): EmailSetting => {
	const rules = { ...current.rules, [key]: next }
	return { on: current.on && anyEmailRule(rules), rules }
}

/**
 * The master flips. Turned on over an empty list (the state the rule above
 * leaves behind), it brings the R213 defaults back, so the switch never
 * reads on with nothing under it.
 */
export const setEmailMaster = (current: EmailSetting, next: boolean): EmailSetting => ({
	on: next,
	rules: next && !anyEmailRule(current.rules) ? emailDefaults() : current.rules
})

// R221 (Esteban, 2 Sep): Brevo replaced Salesforce. "Salesforce will be a huge
// one so we won't prioritize it." The key renamed everywhere with it.
export type CrmProvider = 'brevo' | 'hubspot'

/**
 * One CRM login the workspace holds. Gustavo, 2026-08-26: a connection works
 * like an ad-account credential. The first Connect creates it, and any later
 * form can pick the same account or connect a new one.
 */
export interface CrmAccount {
	id: string
	provider: CrmProvider
	/**
	 * R285 (Esteban, 7 Sep): the organization that owns the CRM account (the
	 * company behind a HubSpot portal or a Brevo account), never a person:
	 * "the name is always organizations, not people". The person who logged
	 * in is `email`, and nothing on screen prints it.
	 */
	name: string
	email: string
	/**
	 * R235 (Gustavo pin #65, 3 Sep): the id the CRM itself shows for the
	 * login (a HubSpot portal id, a Brevo account id). The connected card in
	 * the link wizard prints it beside the account name, never the email.
	 */
	accountId: string
	/**
	 * R279 (Esteban, 4 Sep, item 9): a login the CRM revoked reads Access lost.
	 * R312: a Connections page restores it, not the pill's Reconnect.
	 */
	status: 'connected' | 'lost'
}

/**
 * What a login through the demo OAuth popup hands `addCrmAccount`. R285: the
 * popup names the organization and the id the CRM shows for that account;
 * a login that carries no id gets a pseudo id.
 */
export const toCrmAccount = (c: CrmConnection): CrmAccount => ({
	id: c.id,
	provider: 'hubspot',
	name: c.name,
	email: c.credential_identity || c.name,
	accountId: c.portal_id,
	status: c.status === 'ACTIVE' ? 'connected' : 'lost'
})

export interface CrmAccountInput {
	id?: string
	provider: CrmProvider
	name: string
	email: string
	accountId?: string
}

/** The account id each CRM pushes this form's clean leads to. `null` = none. */
export type CrmConnections = Record<CrmProvider, string | null>

export interface FormRecord {
	verificationMode?: 'static' | 'browser'
	/** Temporary verification proof sent only when saving a new check. */
	crmVerificationToken?: string | null
	slug?: string
	raw?: LeadFormDTO
	captchaEnabled?: boolean
	blockEnabled?: boolean
	id: string
	name: string
	url: string
	selector: string
	status: FormStatus
	lastSeen: string | null

	/** Lifetime counters. There is no date range on this page. */
	submissions: number
	blocked: number
	invalidEmails: number

	emailValidation: boolean
	emailChecks: EmailChecks
	emailRules: Record<string, boolean>
	emailOnFail: FailAction
	/** How long the dashboard keeps a copy of a validated address. */
	retention: RetentionKey

	crm: CrmConnections
	/** R262: the "Flag invalid submissions on my CRM" switch. Undefined (older
	    seeds) follows the connection: a connected CRM reads as on. The wizard
	    writes it; the drawer's Setup tab reads and writes it. */
	crmFlag?: boolean

	captcha: boolean
	captchaProvider: CaptchaProvider
	/** What we DO when the score says bot. Stored, not derived. */
	botAction: BotLevel
	/** WHO the action hits. The second axis, independent of botAction. */
	captchaTrigger: CaptchaTrigger
	/** R278: how low the bot score goes before the block switch acts. Own field.
	    Normal 90%, Strict 80%. */
	botSensitivity: BotSensitivity
	/** R309 (Gustavo pin #81): the CAPTCHA switch's own threshold, Normal 60%,
	    Strict 40%. Its own field too, for the same R262 reason. */
	captchaSensitivity: BotSensitivity
	/** R323 (Gustavo, 13 Sep, item 1): which bad submission starts the block. */
	blockThreshold: BlockThreshold
	captchaOnFail: FailAction

	widgetKnown?: boolean
	widget: boolean
	widgetPlacement: WidgetPlacement
	widgetTheme: WidgetTheme
	widgetLabel: string
}

const defaults = {
	emailChecks: {
		syntax: true,
		domain: true,
		disposable: true,
		roleBased: false
	} as EmailChecks,
	emailRules: emailDefaults(),
	emailOnFail: 'flag' as FailAction,
	// The literal, not DEFAULT_RETENTION: this object is built at module load
	// and the retention block below it has not run yet.
	retention: '90d' as RetentionKey,
	crm: { brevo: null, hubspot: null } as CrmConnections,
	captchaProvider: 'clickguard' as CaptchaProvider,
	// BLOCK, not monitor. These defaults already carry `suspected` + `block`,
	// so every record that spreads them derived to Block before this field
	// existed. 'monitor' here would silently flip six list rows.
	botAction: 'block' as BotLevel,
	captchaTrigger: 'suspected' as CaptchaTrigger,
	botSensitivity: 'normal' as BotSensitivity,
	captchaSensitivity: 'normal' as BotSensitivity,
	blockThreshold: 2 as BlockThreshold,
	captchaOnFail: 'block' as FailAction,
	widgetPlacement: 'above-submit' as WidgetPlacement,
	widgetTheme: 'auto' as WidgetTheme,
	/** Empty means the widget keeps its own live state text. */
	widgetLabel: ''
}

/**
 * The action is STORED now (R69). It used to derive from the trigger, which
 * collapsed the two axes: "CAPTCHA the near-certain bots only" read back as
 * Monitor, because the trigger was not `suspected`. The wizard can reach that
 * pair, so the two must move apart.
 */
export const botLevelOf = (form: Pick<FormRecord, 'botAction'>): BotLevel =>
	form.botAction

/**
 * The three fields one (action, target) pair writes. `captchaOnFail` stays in
 * step with the action so anything that still reads the old field agrees.
 */
export const botLevelPatch = (
	level: BotLevel,
	trigger: CaptchaTrigger
): Pick<FormRecord, 'botAction' | 'captchaTrigger' | 'captchaOnFail'> => ({
	botAction: level,
	captchaTrigger: trigger,
	captchaOnFail: level === 'block' ? 'block' : 'flag'
})

// The organizations the demo OAuth popup lists; `preview-crm-oauth.tsx`
// holds the same names and ids, so a login there lands on the seeded account
// instead of a twin. R285 (Esteban, 7 Sep): accounts carry the organization's
// name, never the person who logged in. The seed forms below pick these by
// id (the ids predate R285 and still say who first logged in; they are keys,
// not copy).
const CRM_ACCOUNT_SEED: CrmAccount[] = [
	{ id: 'crm-hubspot-maya', provider: 'hubspot', name: 'Acme Inc', email: 'maya@acme.co', accountId: '8841220', status: 'connected' },
	{ id: 'crm-brevo-maya', provider: 'brevo', name: 'Acme Inc', email: 'maya@acme.co', accountId: '2201984', status: 'connected' },
	// R279 (Esteban, 4 Sep, item 9): a second HubSpot account whose access the
	// CRM revoked. The Contact us seed sits on it, so the drawer shows the
	// Access lost pill and the Reconnect row.
	{ id: 'crm-hubspot-jordan', provider: 'hubspot', name: 'Harbor Dental', email: 'ads@harbor-dental.example', accountId: '7715903', status: 'lost' }
]

const SEED: FormRecord[] = [
	{
		id: 'demo-request',
		name: 'Demo request',
		url: 'demo.example.com/demo',
		selector: '#demo-form',
		status: 'live',
		lastSeen: '2 min ago',
		submissions: 2148,
		blocked: 412,
		invalidEmails: 96,
		emailValidation: true,
		captcha: true,
		widget: true,
		...defaults,
		crm: { brevo: null, hubspot: 'crm-hubspot-maya' }
	},
	{
		id: 'contact-us',
		name: 'Contact us',
		url: 'demo.example.com/contact',
		selector: 'form.contact',
		status: 'live',
		lastSeen: '14 min ago',
		submissions: 1032,
		blocked: 187,
		invalidEmails: 54,
		emailValidation: true,
		captcha: true,
		widget: false,
		...defaults,
		// R309: Strict on the CAPTCHA fold, Normal on the block fold, so the drawer
		// shows the two folds are independent.
		captchaSensitivity: 'strict',
		// R279: the one seed on a lost login, so the drawer shows Access lost + Reconnect.
		// R294 (Gustavo, pin #74, "add example of a line with an issue flagged"):
		// the list's Flag on CRM cell reads Access lost on this row, in red.
		crm: { brevo: null, hubspot: 'crm-hubspot-jordan' }
	},
	{
		id: 'newsletter',
		name: 'Newsletter signup',
		url: 'demo.example.com/blog',
		selector: '#newsletter',
		status: 'live',
		lastSeen: '1 min ago',
		submissions: 3940,
		blocked: 61,
		invalidEmails: 233,
		emailValidation: true,
		captcha: false,
		widget: false,
		...defaults,
		emailOnFail: 'block'
	},
	{
		id: 'free-trial',
		name: 'Free trial',
		url: 'demo.example.com/pricing/trial',
		selector: '#trial-form',
		status: 'live',
		lastSeen: '6 min ago',
		submissions: 764,
		blocked: 205,
		invalidEmails: 41,
		emailValidation: true,
		captcha: true,
		widget: true,
		...defaults,
		captchaTrigger: 'always',
		// R278: the one seed on Strict, so the drawer shows both states.
		botSensitivity: 'strict',
		// R323: the one seed off the default threshold, so the drawer shows the pick.
		blockThreshold: 3,
		crm: { brevo: null, hubspot: 'crm-hubspot-maya' }
	},
	{
		id: 'quote-request',
		name: 'Quote request',
		url: 'demo.example.com/quote',
		selector: '#quote',
		status: 'paused',
		lastSeen: '3 days ago',
		submissions: 318,
		blocked: 44,
		invalidEmails: 12,
		emailValidation: true,
		captcha: true,
		widget: true,
		...defaults
	},
	{
		id: 'support-ticket',
		name: 'Support ticket',
		url: 'demo.example.com/support',
		selector: 'form#ticket',
		status: 'live',
		lastSeen: '22 min ago',
		submissions: 1507,
		blocked: 96,
		invalidEmails: 0,
		emailValidation: false,
		captcha: true,
		widget: true,
		...defaults,
		// The list shows this one as Monitoring. This is the ONLY seed that
		// overrides the action: the other six inherit Block from `defaults`,
		// which is exactly what they derived to before R69 stored the action.
		botAction: 'monitor' as BotLevel,
		captchaTrigger: 'high-only' as CaptchaTrigger,
		captchaOnFail: 'flag' as FailAction
	},
	{
		id: 'careers-apply',
		name: 'Careers application',
		url: 'demo.example.com/careers',
		selector: '#apply',
		status: 'waiting',
		lastSeen: null,
		submissions: 0,
		blocked: 0,
		invalidEmails: 0,
		emailValidation: false,
		captcha: false,
		widget: false,
		...defaults
	},
	// R158 (Gustavo, pin 51): enough forms that the table paginates for real.
	// Charts and submissions derive from the id hash, so each row is complete.
	{
		id: 'webinar-reg',
		name: 'Webinar registration',
		url: 'demo.example.com/webinar',
		selector: '#webinar-form',
		status: 'live',
		lastSeen: '8 min ago',
		submissions: 1893,
		blocked: 240,
		invalidEmails: 87,
		emailValidation: true,
		captcha: true,
		widget: true,
		...defaults,
		crm: { brevo: null, hubspot: 'crm-hubspot-maya' }
	},
	{
		id: 'ebook-download',
		name: 'Ebook download',
		url: 'demo.example.com/resources/ebook',
		selector: 'form.gated-download',
		status: 'live',
		lastSeen: '31 min ago',
		submissions: 2611,
		blocked: 74,
		invalidEmails: 198,
		emailValidation: true,
		captcha: false,
		widget: false,
		...defaults
	},
	{
		id: 'partner-inquiry',
		name: 'Partner inquiry',
		url: 'demo.example.com/partners',
		selector: '#partner-form',
		status: 'live',
		lastSeen: '2 hr ago',
		submissions: 214,
		blocked: 58,
		invalidEmails: 9,
		emailValidation: true,
		captcha: true,
		widget: true,
		...defaults,
		crm: { brevo: null, hubspot: 'crm-hubspot-maya' }
	},
	{
		id: 'event-rsvp',
		name: 'Event RSVP',
		url: 'demo.example.com/events/summit',
		selector: '#rsvp',
		status: 'live',
		lastSeen: '54 min ago',
		submissions: 977,
		blocked: 131,
		invalidEmails: 45,
		emailValidation: true,
		captcha: true,
		widget: false,
		...defaults
	},
	{
		id: 'beta-waitlist',
		name: 'Beta waitlist',
		url: 'demo.example.com/beta',
		selector: 'form#waitlist',
		status: 'live',
		lastSeen: '3 min ago',
		submissions: 4102,
		blocked: 388,
		invalidEmails: 301,
		emailValidation: true,
		captcha: false,
		widget: false,
		...defaults,
		emailOnFail: 'block' as FailAction
	},
	{
		id: 'sales-contact',
		name: 'Talk to sales',
		url: 'demo.example.com/pricing/sales',
		selector: '#sales-form',
		status: 'live',
		lastSeen: '19 min ago',
		submissions: 542,
		blocked: 97,
		invalidEmails: 22,
		emailValidation: true,
		captcha: true,
		widget: true,
		...defaults,
		botAction: 'monitor' as BotLevel,
		captchaTrigger: 'high-only' as CaptchaTrigger,
		captchaOnFail: 'flag' as FailAction
	},
	{
		id: 'feedback-survey',
		name: 'Feedback survey',
		url: 'demo.example.com/feedback',
		selector: '#survey-form',
		status: 'paused',
		lastSeen: '6 days ago',
		submissions: 689,
		blocked: 12,
		invalidEmails: 31,
		emailValidation: true,
		captcha: false,
		widget: false,
		...defaults
	},
	{
		id: 'whitepaper-download',
		name: 'Whitepaper download',
		url: 'demo.example.com/resources/whitepaper',
		selector: 'form.gated-download',
		status: 'waiting',
		lastSeen: null,
		submissions: 0,
		blocked: 0,
		invalidEmails: 0,
		emailValidation: false,
		captcha: false,
		widget: false,
		...defaults
	}
]

export interface NewFormDraft {
	name: string
	url: string
	selector: string
	/** Protection settings picked in the link flow, layered over the defaults. */
	settings?: Partial<FormRecord>
}

interface FormSpamStore {
	api?: LeadFormsApi
	setup: Setup | null
	loading: boolean
	error: string
	canWrite: boolean
	refresh: () => Promise<void>
	/** The global install gate. One code, every form. */
	installed: boolean
	setInstalled: (value: boolean) => void
	forms: FormRecord[]
	getForm: (id: string) => FormRecord | undefined
	addForm: (draft: NewFormDraft) => Promise<FormRecord>
	updateForm: (id: string, patch: Partial<FormRecord>) => Promise<void>
	removeForm: (id: string) => Promise<void>
	/** Every CRM login the workspace holds, shared across forms. */
	crmAccounts: CrmAccount[]
	/** Adds a login, or returns the one that already holds that email. */
	addCrmAccount: (draft: CrmAccountInput) => CrmAccount
	/**
	 * R279 (Esteban, 4 Sep, item 9): flips one login between `connected` and
	 * `lost`. R312: a Connections page writes `connected` back, not the pill's
	 * Reconnect.
	 */
	setCrmAccountStatus: (id: string, status: CrmAccount['status']) => void
}

const FormSpamContext = createContext<FormSpamStore | null>(null)

const slug = (value: string) =>
	value
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-|-$/g, '')
		.slice(0, 40) || 'form'

const PreviewFormSpamProvider = ({
	children
}: {
	children: React.ReactNode
}) => {
	// The gate starts OPEN so a reload lands on the forms list, the state the
	// demo is really about. The gate view keeps its own URL at /start.
	const [installed, setInstalled] = useState(true)
	const [forms, setForms] = useState<FormRecord[]>(SEED)
	const [crmAccounts, setCrmAccounts] = useState<CrmAccount[]>(CRM_ACCOUNT_SEED)
	// A mirror the add can read at once: the caller needs the id back in the
	// same tick to pick the account for its form.
	const crmAccountsRef = useRef(crmAccounts)
	crmAccountsRef.current = crmAccounts

	const getForm = useCallback(
		(id: string) => forms.find((f) => f.id === id),
		[forms]
	)

	const addForm = useCallback(async (draft: NewFormDraft) => {
		// A linked form starts in `waiting`: it goes live the first time the
		// script reports a submission from that selector.
		const record: FormRecord = {
			id: `${slug(draft.name)}-${Math.random().toString(36).slice(2, 6)}`,
			name: draft.name.trim(),
			url: draft.url.trim(),
			selector: draft.selector.trim(),
			status: 'waiting',
			lastSeen: null,
			submissions: 0,
			blocked: 0,
			invalidEmails: 0,
			emailValidation: false,
			captcha: false,
			widget: false,
			...defaults,
			...draft.settings
		}
		setForms((current) => [record, ...current])
		return record
	}, [])

	const updateForm = useCallback(async (id: string, patch: Partial<FormRecord>) => {
		setForms((current) =>
			current.map((f) => (f.id === id ? { ...f, ...patch } : f))
		)
	}, [])

	const removeForm = useCallback(async (id: string) => {
		setForms((current) => current.filter((f) => f.id !== id))
	}, [])

	const addCrmAccount = useCallback((draft: CrmAccountInput) => {
		const email = draft.email.trim().toLowerCase()
		const accountId = draft.accountId?.trim() || null
		// R285: the CRM's own account id is the identity when the login carries
		// one; the email stands in for a login that carries none.
		const held = crmAccountsRef.current.find(
			(a) =>
				a.provider === draft.provider &&
				(accountId ? a.accountId === accountId : a.email.toLowerCase() === email)
		)
		if (held) {
			// R279 (Esteban, 4 Sep, item 9): a fresh login through the popup
			// is a reconnect, so a held account on `lost` reads connected again.
			if (held.status === 'connected') return held
			const restored: CrmAccount = { ...held, status: 'connected' }
			crmAccountsRef.current = crmAccountsRef.current.map((a) => (a.id === held.id ? restored : a))
			setCrmAccounts(crmAccountsRef.current)
			return restored
		}
		const account: CrmAccount = {
			id: `crm-${draft.provider}-${Math.random().toString(36).slice(2, 6)}`,
			provider: draft.provider,
			name: draft.name.trim(),
			email,
			// R235: a login with no account id gets a 7-digit pseudo id. R285:
			// the demo popup now sends the CRM's own, so this is the fallback.
			accountId: accountId ?? String(1000000 + Math.floor(Math.random() * 9000000)),
			status: 'connected'
		}
		crmAccountsRef.current = [...crmAccountsRef.current, account]
		setCrmAccounts(crmAccountsRef.current)
		return account
	}, [])

	// R279 (Esteban, 4 Sep, item 9). R312: a Connections page restores a lost
	// login through this, not the pill's Reconnect. The ref takes the change
	// first, the same as `addCrmAccount`, so a read in the same tick sees it.
	const setCrmAccountStatus = useCallback((id: string, status: CrmAccount['status']) => {
		crmAccountsRef.current = crmAccountsRef.current.map((a) => (a.id === id ? { ...a, status } : a))
		setCrmAccounts(crmAccountsRef.current)
	}, [])

	const value = useMemo<FormSpamStore>(
		() => ({
			setup: null, loading: false, error: '', canWrite: true, refresh: async () => undefined,
			installed,
			setInstalled,
			forms,
			getForm,
			addForm,
			updateForm,
			removeForm,
			crmAccounts,
			addCrmAccount,
			setCrmAccountStatus
		}),
		[installed, forms, getForm, addForm, updateForm, removeForm, crmAccounts, addCrmAccount, setCrmAccountStatus]
	)

	return (
		<FormSpamContext.Provider value={value}>
			{children}
		</FormSpamContext.Provider>
	)
}

export const toFormRecord = (raw: LeadFormDTO): FormRecord => ({
	...defaults,
	raw,
	id: raw.id,
	slug: raw.slug,
	name: raw.form_name,
	url: raw.form_url,
	verificationMode: raw.verification_mode ?? 'static',
	selector: raw.form_selector || '',
	status: raw.total_form_submissions ? 'live' : 'waiting',
	lastSeen: null,
	submissions: raw.total_form_submissions,
	invalidEmails: raw.total_flagged_submissions,
	blocked: raw.total_blocked_bots,
	emailValidation: raw.protect_ads.active,
	emailRules: Object.fromEntries(
		Object.keys(emailDefaults()).map((key) => [
			key,
			raw.protect_ads.selected_statuses.includes(key as LeadFormEmailFlagDTO)
		])
	),
	blockThreshold: raw.protect_ads.threshold as BlockThreshold,
	captcha: raw.block_bots.active,
	captchaEnabled: raw.block_bots.captcha,
	blockEnabled: raw.block_bots.block,
	captchaSensitivity: raw.block_bots.captcha_sensitivity,
	botSensitivity: raw.block_bots.block_sensitivity,
	botAction: raw.block_bots.block
		? 'block'
		: raw.block_bots.captcha
		? 'challenge'
		: 'monitor',
	widget: false,
	widgetKnown: false,
	crmFlag: raw.flag_crm.active,
	crm: {
		hubspot: raw.flag_crm.crm === 'hubspot' ? raw.flag_crm.connection_id : null,
		brevo: raw.flag_crm.crm === 'brevo' ? raw.flag_crm.connection_id : null
	}
})
export const toFormDraft = (form: FormRecord): FormDraft => ({
	form_name: form.name,
	form_url: form.url,
	form_selector: form.selector || null,
	verification_mode: form.verificationMode ?? 'static',
	protect_ads: {
		active: form.emailValidation,
		selected_statuses: Object.keys(form.emailRules).filter(
			(key) => form.emailRules[key]
		) as LeadFormEmailFlagDTO[],
		threshold: form.blockThreshold
	},
	block_bots: {
		active: form.captcha,
		captcha: form.captchaEnabled ?? form.botAction !== 'monitor',
		block: form.blockEnabled ?? form.botAction === 'block',
		captcha_sensitivity: form.captchaSensitivity,
		block_sensitivity: form.botSensitivity
	},
	flag_crm: { active: !!form.crmFlag, crm: form.crm.hubspot ? 'hubspot' : null, connection_id: form.crm.hubspot, ...(form.crmVerificationToken ? { verification_token: form.crmVerificationToken } : {}) }
})
const LiveFormSpamProvider = ({
	children,
	api
}: {
	children: React.ReactNode
	api: LeadFormsApi
}) => {
	const [forms, setForms] = useState<FormRecord[]>([])
	const [crmAccounts, setCrmAccounts] = useState<CrmAccount[]>([])
	const [setup, setSetup] = useState<Setup | null>(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')
	const epoch = useRef(0)
	const refresh = useCallback(async () => {
		const token = ++epoch.current
		setLoading(true)
		setError('')
		try {
			const [rows, settings, connections] = await Promise.all([api.list(), api.settings(), api.accountId ? createCrmApi(api.accountId).list() : Promise.resolve({connections: []})])
			if (token === epoch.current) {
				setForms((current) =>
					rows.map((row) => {
						const mapped = toFormRecord(row)
						const prior = current.find((f) => f.id === row.id)
						return prior && prior.url === mapped.url && prior.selector === mapped.selector && prior.slug === mapped.slug
							? {
									...mapped,
									widget: prior.widget,
									widgetKnown: prior.widgetKnown
							  }
							: mapped
					})
				)
				setSetup(settings)
				setCrmAccounts(connections.connections.map(toCrmAccount))
			}
		} catch (e) {
			if (token === epoch.current)
				setError(e instanceof Error ? e.message : 'Unable to load lead forms')
		} finally {
			if (token === epoch.current) setLoading(false)
		}
	}, [api])
	useEffect(() => {
		void refresh()
		return () => {
			epoch.current++
		}
	}, [refresh])
	const write = () => {
		if (!setup?.can_write)
			throw new Error('You do not have permission to change forms')
		epoch.current++
		setLoading(false)
	}
	const value: FormSpamStore = {
		api,
		setup,
		loading,
		error,
		canWrite: !!setup?.can_write,
		refresh,
		installed: true,
		setInstalled: () => undefined,
		forms,
		getForm: (id) => forms.find((f) => f.id === id),
		addForm: async (draft) => {
			write()
			const settings = draft.settings || {}
			const record = {
				...defaults,
				id: '',
				name: draft.name,
				url: draft.url,
				selector: draft.selector,
				emailValidation: true,
				captcha: true,
				...settings
			} as FormRecord
			const created = {
				...toFormRecord(await api.create(toFormDraft(record))),
				widget: !!settings.widget,
				widgetKnown: !!settings.captcha
			}
			setForms((rows) => [created, ...rows])
			return created
		},
		updateForm: async (id, patch) => {
			write()
			const current = forms.find((f) => f.id === id)
			if (!current) throw new Error('Form no longer exists')
			if (
				Object.keys(patch).every(
					(key) => key === 'widget' || key === 'widgetKnown'
				)
			) {
				setForms((rows) =>
					rows.map((row) =>
						row.id === id ? { ...row, ...patch, widgetKnown: true } : row
					)
				)
				return
			}
			const mapped = toFormRecord(await api.update(id, toFormDraft({ ...current, ...patch })))
			const sameIdentity = current.url === mapped.url && current.selector === mapped.selector && current.slug === mapped.slug
			const updated = { ...mapped, widget: sameIdentity ? current.widget : false, widgetKnown: sameIdentity ? current.widgetKnown : false }

			setForms((rows) => rows.map((row) => (row.id === id ? updated : row)))
		},
		removeForm: async (id) => {
			write()
			await api.remove(id)
			setForms((rows) => rows.filter((row) => row.id !== id))
		},
		crmAccounts,
		addCrmAccount: (draft) => {
			if (!draft.id) throw new Error('HubSpot connection did not return an ID')
			const item: CrmAccount = { ...draft, id: draft.id, accountId: draft.accountId || '', status: 'connected' }
			setCrmAccounts(rows => [...rows.filter(row => row.id !== item.id), item])
			return item
		},
		setCrmAccountStatus: () => undefined
	}
	return (
		<FormSpamContext.Provider value={value}>
			{children}
		</FormSpamContext.Provider>
	)
}
export const FormSpamProvider = ({
	children,
	api
}: {
	children: React.ReactNode
	api?: LeadFormsApi
}) =>
	api ? (
		<LiveFormSpamProvider api={api}>{children}</LiveFormSpamProvider>
	) : (
		<PreviewFormSpamProvider>{children}</PreviewFormSpamProvider>
	)

export const useFormSpam = (): FormSpamStore => {
	const store = useContext(FormSpamContext)
	if (!store)
		throw new Error('useFormSpam must be used inside FormSpamProvider')
	return store
}

/** Status pill copy + tone, shared by the table and the detail header. */
export const STATUS_META: Record<
	FormStatus,
	{ label: string; tone: 'success' | 'warning' | 'neutral' }
> = {
	live: { label: 'Active', tone: 'success' },
	waiting: { label: 'Waiting for traffic', tone: 'warning' },
	paused: { label: 'Paused', tone: 'neutral' }
}

/** A form counts as protected when it is live and at least one check is on. */
export const isProtected = (form: FormRecord) =>
	form.status === 'live' &&
	(form.emailValidation || form.captcha || form.widget)

/**
 * A live form needs setup when the widget is not on the page, or when every
 * check is off. R69 widened this: the widget is what the visitor actually
 * meets, so a form without it is not protected, whatever else is switched on.
 *
 * R260 (Esteban, 3 Sep): "There are forms that say 'needs setup' and they're
 * here right, filtered. But there's no indicator inside the drawer or nothing,
 * you should like mark it somehow." The rule moved here from the page, so the
 * pill count, the status cell and the drawer badge read ONE definition.
 */
export const needsSetup = (form: FormRecord) =>
	form.status !== 'paused' &&
	((form.captcha && form.widgetKnown !== false && !form.widget) || (!form.emailValidation && !form.captcha && !form.crmFlag))

/** Why the form needs setup. The drawer badge says it in its tooltip. */
export const setupReason = (form: FormRecord) =>
	!form.widget
		? 'The verification widget is not on the page yet.'
		: 'Every protection is off.'

/**
 * R272 (Esteban, 4 Sep): "there should be a red text or indicator in the
 * specific container and thing that tells you what needs to be set up". The
 * drawer prints these inside the Setup panes; `setupReason` above stays the
 * badge's short form. Both read the one `needsSetup` rule.
 */
export const SETUP_WARN = {
	/** The bot pane header, while the record says the widget is absent. */
	widget: 'The verification widget is not on the page yet. Paste the snippet below, then check the page.',
	/** The line right above the widget step, same case. */
	widgetStep: 'The widget is not on the page yet.',
	/** Both protection pane headers, while both master switches are off. */
	off: 'Every protection is off. Turn on at least one.'
} as const

/**
 * R252 / R270 (Esteban, 4 Sep): the rows-per-page stops of the DS
 * `Pagination`. The DS story steps (10 / 25 / 50) plus the 5 both tables
 * open at. One list for the forms table and the drawer feed: "this
 * pagination thing should be inside the drawer table too".
 */
export const PAGE_SIZE_OPTIONS = [5, 10, 25, 50]

/**
 * R260: the status cell's third word. Paused wins (a paused form is not asked
 * to finish anything), then Needs setup, then Active. The label is the pill's
 * word, the tone is the one `STATUS_META.waiting` already uses for "not done".
 */
export const NEEDS_SETUP_META = { label: 'Needs setup', tone: 'warning' as const }
// R291 (Esteban, 10 Sep): the Protection status column left the list, so no
// caller renders this now. It stays for the day the column returns.
export const protectionMeta = (form: FormRecord) =>
	form.status === 'paused'
		? STATUS_META.paused
		: needsSetup(form)
			? NEEDS_SETUP_META
			: STATUS_META.live

export const formatCount = (value: number) => value.toLocaleString('en-US')

/* ------------------------------------------------------------------ */
/* Round 5: the detail drawer's data.                                 */
/*                                                                    */
/* Everything below is DERIVED and DETERMINISTIC. A seeded generator  */
/* (keyed on the form id) replaces Math.random, so a re-render, a     */
/* range switch or a screenshot always sees the same numbers, and the */
/* trend, the mix and the totals all add up to each other.            */
/* ------------------------------------------------------------------ */

export type SubmissionStatus = 'passed' | 'flagged' | 'blocked'

export interface VerdictPoint {
	/** Short axis label, e.g. "Aug 18". */
	day: string
	/** Approved submissions that day. */
	allow: number
	/** Blocked bots that day. */
	block: number
	/** Challenged (captcha raised) that day. */
	challenge: number
}

// R295 (Gustavo, 10 Sep, pin #96): the drawer table gains a Traffic Source
// column and filter, so every feed row names the ad network it came from.
export type TrafficSource = 'Google Ads' | 'Meta Ads' | 'Microsoft Ads' | 'Other'
/** Every traffic source a feed row can carry, in display order. */
export const TRAFFIC_SOURCES: ReadonlyArray<TrafficSource> = [
	'Google Ads',
	'Meta Ads',
	'Microsoft Ads',
	'Other'
]

export interface SubmissionRow {
	id: string
	/**
	 * The form this row came in through. R298 (Gustavo, 11 Sep, pin #100): the
	 * page's Submissions tab lists every form's rows in one table, so each row
	 * must name its owner for the Form column and the Form filter.
	 */
	formId: string
	/** "Aug 18" — the calendar day. */
	day: string
	/** "09:42" — the clock time. */
	clock: string
	status: SubmissionStatus
	/**
	 * R104: what email validation decided, by key. It is finer than `status`
	 * and it agrees with it: every outcome names exactly one verdict. The
	 * Status column pill reads this, the three filter pills read `status`.
	 */
	outcome: EmailOutcomeKey
	/** The address the visitor typed into the form's email field. */
	email: string
	ip: string
	/** "1m 40s" | "4s" | "Bounced" */
	timeOnPage?: string
	/** The sub-line under the time: what the visitor did. */
	interaction?: string
	network?: 'Clean' | 'Proxy' | 'Data center' | 'VPN'
	/** "Heritage Pines, US" */
	location: string
	/** ISO 3166 alpha-2 code of the location, for the DS `Flag` (R316: no emoji). */
	country: string
	/** The ad network the visitor came from. R295 (Gustavo, pin #96). */
	source: TrafficSource
}

/** FNV-ish string hash: one stable seed per form id. */
const hashId = (value: string) => {
	let h = 2166136261
	for (let i = 0; i < value.length; i++) {
		h ^= value.charCodeAt(i)
		h = Math.imul(h, 16777619)
	}
	return h >>> 0
}

/** Seeded LCG in [0, 1). The same seed always plays the same tape. */
const makeRng = (seed: number) => {
	let state = seed || 1
	return () => {
		state = (Math.imul(state, 1664525) + 1013904223) >>> 0
		return state / 4294967296
	}
}

/** The 30 calendar days that end on "today" (Aug 18) for the mock. */
const DAY_LABELS = Array.from({ length: 30 }, (_, i) =>
	i < 12 ? `Jul ${20 + i}` : `Aug ${i - 11}`
)

/**
 * The last `days` calendar days, as a set of `day` labels. R298 (Gustavo, 11
 * Sep, pin #100): the page's Submissions table cuts its rows to the 7/30 Days
 * control in the page header, and a row's `day` is the only date it carries,
 * so the cut is a set lookup on the same labels the series use.
 */
export const daysInRange = (days: number): ReadonlySet<string> =>
	new Set(DAY_LABELS.slice(-days))

const seriesCache = new Map<string, VerdictPoint[]>()

/**
 * The full 30-day verdict series for one form. The 7-day view slices the
 * tail, so the two ranges always agree on the shared days.
 */
export const getVerdictSeries = (form: FormRecord): VerdictPoint[] => {
	const cached = seriesCache.get(form.id)
	if (cached) return cached

	// A form that has never been submitted has no history to draw. The days
	// stay so the axis keeps its shape, but every count is zero: the chart,
	// the mix and the tiles then say the same thing the list row says.
	if (form.submissions === 0) {
		const blank = DAY_LABELS.map((day) => ({
			day,
			allow: 0,
			block: 0,
			challenge: 0
		}))
		seriesCache.set(form.id, blank)
		return blank
	}

	const rng = makeRng(hashId(form.id))
	// Lifetime shares anchor the mock so the drawer agrees with the list.
	// The floor is a divide guard only; it never invents volume now.
	const total = Math.max(form.submissions, 40)
	const blockShare = Math.min(0.55, Math.max(0.04, form.blocked / total))
	const challengeShare = Math.min(
		0.3,
		Math.max(0.03, form.invalidEmails / total + 0.05)
	)

	const points = DAY_LABELS.map((day, i) => {
		// A quiet weekly swell plus noise, so the line has a shape.
		const swell = 1 + 0.35 * Math.sin((i / 30) * Math.PI * 2.2)
		const volume = Math.max(4, Math.round((total / 34) * swell * (0.7 + 0.6 * rng())))
		const block = Math.round(volume * blockShare * (0.6 + 0.8 * rng()))
		const challenge = Math.round(volume * challengeShare * (0.6 + 0.8 * rng()))
		const allow = Math.max(1, volume - block - challenge)
		return { day, allow, block, challenge }
	})

	seriesCache.set(form.id, points)
	return points
}

export interface VerdictTotals {
	allow: number
	block: number
	challenge: number
	total: number
}

export const verdictTotals = (points: VerdictPoint[]): VerdictTotals => {
	const allow = points.reduce((sum, p) => sum + p.allow, 0)
	const block = points.reduce((sum, p) => sum + p.block, 0)
	const challenge = points.reduce((sum, p) => sum + p.challenge, 0)
	return { allow, block, challenge, total: allow + block + challenge }
}

/**
 * R142 (Gustavo, pin 42). The three table counts, one period earlier.
 *
 * The same rule as `previousOutcomeTotals`: the mock holds 30 days and nothing
 * before them, so the earlier period is drawn from the seeded generator, per
 * verdict, at 0.65 to 1.35 of the count we really have. The seed carries the
 * form and the range, so a range switch really moves the answer and a reload
 * does not. `total` is the sum of the three drawn parts, so the change on
 * Form submissions is arithmetic on the same numbers as its two slices.
 */
export const previousVerdictTotals = (
	totals: VerdictTotals,
	seed: string
): VerdictTotals => {
	const draw = (key: 'allow' | 'block' | 'challenge') => {
		const rng = makeRng(hashId(`${seed}/prev/${key}`))
		return Math.round(totals[key] * (0.65 + rng() * 0.7))
	}
	const allow = draw('allow')
	const block = draw('block')
	const challenge = draw('challenge')
	return { allow, block, challenge, total: allow + block + challenge }
}

export const formatShare = (part: number, total: number) =>
	total === 0 ? '0.0%' : `${((part / total) * 100).toFixed(1)}%`

/**
 * R69. How much of the traffic one TARGET reaches, as a share of every
 * submission the account already has. It reads the same seeded series the
 * charts draw, so the wizard number and the dashboard never disagree.
 *
 * It takes the trigger only. The number depends on WHO is caught, not on what
 * happens to them, and the caller writes the sentence around it.
 *
 * Returns null when there is nothing honest to say. The caller hides the line.
 * Known floor: getVerdictSeries clamps the block share at 0.04, so the answer
 * never falls under about 4 percent. That is the seed, not a bug to paper over.
 */
export const reachEstimate = (
	forms: FormRecord[],
	trigger: CaptchaTrigger
): string | null => {
	const withData = forms.filter(f => f.submissions > 0)
	if (withData.length === 0) return null

	let allow = 0
	let block = 0
	let challenge = 0
	let total = 0
	withData.forEach(f => {
		const t = verdictTotals(getVerdictSeries(f))
		allow += t.allow
		block += t.block
		challenge += t.challenge
		total += t.total
	})
	if (total === 0) return null

	const reached =
		trigger === 'high-only'
			? block
			: trigger === 'suspected'
			? block + challenge
			: // The borderline tail: what `always` adds over `suspected`.
			  block + challenge + Math.round(allow * 0.03)

	return formatShare(reached, total)
}

/* ---------------------- per-layer outcome series ---------------------- */
/* Round 30 (Gustavo's analytics spec): each protection layer gets its own
   outcome breakdown. Both series derive from getVerdictSeries, so the
   layer charts, the overall charts and the header tiles all add up. */

export interface OutcomeDef {
	key: string
	label: string
	color: string
	/**
	 * R232 (Gustavo, 3 Sep, pin #64): one line that says what the outcome
	 * means. The chart-head tooltip prints these, one row per outcome, in
	 * place of the old paragraph. The email rows read the same words the
	 * email-rule toggles show, so the two never drift.
	 */
	desc?: string
	/**
	 * R108: a RISE in this outcome is bad news.
	 *
	 * The legend prints a change against the period before. On most rows that
	 * change is purple and takes no side, because a category simply moved. On a
	 * row marked here it takes one: a rise is the danger token, a fall is the
	 * success token.
	 *
	 * Set it only where the direction is really a judgement. `Unchecked`
	 * counts how we were configured, not how the traffic behaved, so it stays
	 * neutral. The email layer sets it by hand ONCE, to `false` on Catch-all
	 * (R225): everywhere else `verdict` already says the same thing, and two
	 * lists kept by hand would drift apart. Read it through `isWorseWhenUp`,
	 * never straight off the def.
	 */
	worseWhenUp?: boolean
}

/**
 * CAPTCHA outcomes, stack order bottom-up. The legend carries the short
 * names; `desc` carries the one-line meaning the chart-head tooltip prints
 * (R232).
 *
 * R227 (Gustavo, 2 Sep, pin kf4aqi): every CAPTCHA row names the visitor
 * first, "Suspicious visitor, CAPTCHA failed", because a bare "CAPTCHA failed"
 * read as our CAPTCHA that failed to load. Only a suspicious visitor meets the
 * CAPTCHA, so the three CAPTCHA rows carry the words; a normal visitor and a
 * high-probability bot never see one. Highlight and dim key on the label on
 * both sides, so the rename touches nothing else.
 *
 * R229 (Esteban, 2 Sep): "make the labels shorter somehow so it's actually one
 * line". The R227 labels run to 204px, and a 277px half column with 154px of
 * numbers leaves 85px, 67px in the tray at 1440. Each label is one word now,
 * 61px at the widest, and each names the visitor's verdict, which was the
 * point of R227: "Failed" beside "Verified" and "Blocked" is the visitor who
 * failed the CAPTCHA, not a CAPTCHA that failed to load. The one-line
 * meanings below stand in the chart-head tooltip (R232). Highlight and dim
 * still key on the label.
 *
 * R234 (Gustavo, pin #61, P0; Esteban, 3 Sep): a bare "Failed" still read as
 * our CAPTCHA that failed to load. Esteban asked for "Captcha failed by
 * visitor, or something like that". That phrase is 133px, and the label slot
 * in a legend row is 67px at 1440 (259px row, 154px of numbers, the DS 16px
 * gap and 10px swatch): every form that names the visitor clips, "Visitor
 * failed" by 1px. "Not verified" is 64px, it is the plain opposite of
 * "Verified" beside it, and it states a result about the visitor, so nothing
 * in it can read as our system that failed. "Unchecked" stays the row where
 * no CAPTCHA ran at all; the chart-head glossary tells the two apart. The
 * other four rows stay one word.
 *
 * R274 (Esteban, 4 Sep): "not sure that 'verified' 'unverified' 'unchecked'
 * actually explain the paradigm". One word a row hid the model: three score
 * tiers, and only the middle tier meets a CAPTCHA. The three middle rows now
 * name the CAPTCHA and what the visitor did with it: "CAPTCHA solved",
 * "CAPTCHA off", "CAPTCHA unsolved". "Solve" is a verb only a visitor can do,
 * so "unsolved" can never read as our CAPTCHA that failed to load, which is
 * what Gustavo (pin #61) read into "failed". The shared prefix makes the
 * middle tier read as one group between "Normal" and "Blocked". The labels
 * run to about 105px at 12px, so the bot legend stands in ONE column
 * (`OutcomeColumn legendColumns`); he chose that cost over the one-word
 * labels R229 had asked for.
 */
export const BOT_OUTCOMES: OutcomeDef[] = [
	// R125 (Gustavo, pin byg36t): the labels name what happened to the visitor
	// in CAPTCHA terms, and the stack runs good to bad, bottom-up. "CAPTCHA
	// disabled" sits in the middle in yellow: it is neither a pass nor a block,
	// it is a visitor we could not check.
	{ key: 'clean', label: 'Normal', color: '#17b26a', desc: 'Low bot score, so no CAPTCHA was needed' },
	{ key: 'passed', label: 'CAPTCHA solved', color: '#067647', desc: 'Suspicious visitor who solved the CAPTCHA' },
	{ key: 'off', label: 'CAPTCHA off', color: '#fac515', desc: 'Suspicious visitor, but the CAPTCHA was off, so nothing was checked' },
	{ key: 'failed', label: 'CAPTCHA unsolved', color: '#f04438', worseWhenUp: true, desc: 'Visitor who failed to solve the CAPTCHA (submission blocked)' },
	{
		key: 'blockedHigh',
		label: 'Blocked',
		color: '#b42318',
		worseWhenUp: true,
		desc: 'Bot score too high, so the submission was blocked at the form'
	}
]

/** The seven email validation outcomes, by key. */
export type EmailOutcomeKey =
	| 'valid'
	| 'alias'
	| 'catchAll'
	| 'invalid'
	| 'spamTrap'
	| 'doNotMail'
	| 'unknown'

/**
 * R104: the email layer's outcome carries two more things than the chart
 * needs, because the submissions table names the outcome per row now.
 *
 * `verdict` is what the outcome DOES to the submission. It is on the table
 * and not in a second map, so a new outcome cannot be added without saying
 * whether we keep it, mark it or stop it. The three filter pills still read
 * the row's own verdict, so the two must never disagree.
 *
 * `tone` is the DS Label colour. The DS gives five (gray, brand, success,
 * warning, error) and the layer has seven outcomes, so the tone follows the
 * VERDICT and the pill text carries the name. The reader sees at a glance
 * whether we kept the submission, and reads one word to learn why.
 */
export interface EmailOutcomeDef extends OutcomeDef {
	key: EmailOutcomeKey
	verdict: SubmissionStatus
	tone: 'success' | 'warning' | 'error' | 'gray'
}

/** Email validation outcomes, stack order bottom-up. R177 (Gustavo,
 * 2026-09-01): "these were not supposed to be purple", his correction of
 * his own pin #54 (R157 put five neutrals on a purple ramp). Each row now
 * carries its verdict color, the semantic palette BOT_OUTCOMES already
 * uses; Do not mail returns to pin d7w338's orange (R125). Color says the
 * verdict, the word says why. */
/** R232: the chart-head glossary reads the email-rule toggle's own words. */
const ruleDesc = (key: string) => EMAIL_RULES.find((rule) => rule.key === key)?.desc ?? ''

export const EMAIL_OUTCOMES: EmailOutcomeDef[] = [
	// Valid has no toggle (nothing to flag), so its line is written here.
	{ key: 'valid', label: 'Valid', color: '#17b26a', verdict: 'passed', tone: 'success', desc: 'Address exists and the inbox accepts mail' },
	{ key: 'alias', label: 'Alias', color: '#067647', verdict: 'passed', tone: 'success', desc: ruleDesc('alias') },
	{
		key: 'catchAll',
		label: 'Catch-all',
		color: '#fac515',
		verdict: 'flagged',
		tone: 'warning',
		desc: ruleDesc('catchAll'),
		// R225 (Gustavo, 2 Sep, pin ked64q): "The Catch-all category growth
		// indicator can be in purple". A catch-all domain accepts every address,
		// so the check could not tell, the same way `off` could not check a
		// visitor. Neither direction is a judgement, so the legend takes no side.
		// The verdict stays `flagged`: the row is still yellow, still a flag on
		// the submission. Only the change indicator goes neutral.
		worseWhenUp: false
	},
	{ key: 'invalid', label: 'Invalid', color: '#f04438', verdict: 'blocked', tone: 'error', desc: ruleDesc('invalid') },
	{
		key: 'spamTrap',
		label: 'Spam trap',
		color: '#b42318',
		verdict: 'blocked',
		tone: 'error',
		desc: ruleDesc('spamTraps')
	},
	{
		key: 'doNotMail',
		label: 'Do not mail',
		color: '#f79009',
		verdict: 'flagged',
		tone: 'warning',
		desc: ruleDesc('doNotMail')
	},
	{ key: 'unknown', label: 'Unknown', color: '#98a2b3', verdict: 'flagged', tone: 'gray', desc: ruleDesc('unknown') }
]

/** One outcome, by key. The table is seven long, so a find is enough. */
export const emailOutcome = (key: EmailOutcomeKey): EmailOutcomeDef =>
	EMAIL_OUTCOMES.find((o) => o.key === key) as EmailOutcomeDef

/**
 * The outcomes a verdict can come from, in the legend's own order. Derived,
 * never written by hand: the verdict lives on the outcome table above.
 */
const OUTCOMES_BY_VERDICT: Record<SubmissionStatus, EmailOutcomeKey[]> = {
	passed: EMAIL_OUTCOMES.filter((o) => o.verdict === 'passed').map((o) => o.key),
	flagged: EMAIL_OUTCOMES.filter((o) => o.verdict === 'flagged').map((o) => o.key),
	blocked: EMAIL_OUTCOMES.filter((o) => o.verdict === 'blocked').map((o) => o.key)
}

export interface OutcomePoint {
	day: string
	counts: Record<string, number>
}

const botCache = new Map<string, OutcomePoint[]>()

/**
 * Sessions per day by CAPTCHA outcome. Sessions run higher than
 * submissions: visits that bounce never submit but still get scored. The
 * first few days count triggered sessions as "off" (the check went live a
 * few days into the window), and a form with the toggle off stays "off"
 * for the whole window.
 */
export const getBotOutcomeSeries = (form: FormRecord): OutcomePoint[] => {
	// R69: the DEGREE decides which bars exist, not the master switch alone.
	// Before this, a Monitor form still drew "High bot probability, blocked"
	// (R125 name; it was "Blocked outright" then), because the
	// gate only read `form.captcha`. Monitor stops nobody, so every triggered
	// session belongs in "off". CAPTCHA stops nobody outright, so the block
	// pool joins the challenge pool instead of drawing its own bar.
	const level = botLevelOf(form)
	const acts = form.captcha && level !== 'monitor'

	// The key holds the master switch and the degree. Both change the bars, and
	// a key of the id alone served the first answer for ever.
	const cacheKey = `${form.id}:${form.captcha ? 'on' : 'off'}:${level}`
	const cached = botCache.get(cacheKey)
	if (cached) return cached

	const rng = makeRng(hashId(form.id + ':bot'))
	const liveFrom = 4 + Math.floor(rng() * 3)
	const points = getVerdictSeries(form).map((p, i) => {
		const submitted = p.allow + p.block + p.challenge
		const extra = Math.round(submitted * (0.25 + 0.3 * rng()))
		const clean = p.allow + extra
		const triggered = p.block + p.challenge
		// The draw is consumed on every day, so the series does not shift when
		// the user changes the degree. Only the split below changes.
		const roll = 0.15 + 0.2 * rng()
		if (!acts || i < liveFrom) {
			return {
				day: p.day,
				counts: { clean, passed: 0, failed: 0, blockedHigh: 0, off: triggered }
			}
		}
		const challenged = level === 'block' ? p.challenge : triggered
		const failed = Math.round(challenged * roll)
		return {
			day: p.day,
			counts: {
				clean,
				passed: challenged - failed,
				failed,
				blockedHigh: level === 'block' ? p.block : 0,
				off: 0
			}
		}
	})

	botCache.set(cacheKey, points)
	return points
}

const emailCache = new Map<string, OutcomePoint[]>()

/**
 * Submissions per day by email validation outcome. The invalid share
 * tracks the form's lifetime count; a form with the toggle off has no
 * verdicts, so every submission lands in "unknown".
 */
export const getEmailOutcomeSeries = (form: FormRecord): OutcomePoint[] => {
	// R150 (audit 2026-08-28): the key holds the toggle. A key of the id alone
	// served the first answer for ever, so an Email validation switch in the
	// drawer never moved this chart. The bot series keys the same way.
	const cacheKey = `${form.id}:${form.emailValidation ? 'on' : 'off'}`
	const cached = emailCache.get(cacheKey)
	if (cached) return cached

	const rng = makeRng(hashId(form.id + ':email'))
	const lifetime = Math.max(form.submissions, 40)
	const invalidShare = Math.min(0.3, Math.max(0.03, form.invalidEmails / lifetime))
	const points = getVerdictSeries(form).map((p) => {
		const volume = p.allow + p.block + p.challenge
		if (!form.emailValidation) {
			return {
				day: p.day,
				counts: {
					valid: 0,
					alias: 0,
					catchAll: 0,
					invalid: 0,
					spamTrap: 0,
					doNotMail: 0,
					unknown: volume
				}
			}
		}
		const invalid = Math.round(volume * invalidShare * (0.6 + 0.8 * rng()))
		const catchAll = Math.round(volume * 0.08 * (0.5 + rng()))
		const alias = Math.round(volume * 0.05 * (0.5 + rng()))
		const spamTrap = Math.round(volume * 0.02 * rng())
		const doNotMail = Math.round(volume * 0.04 * (0.5 + rng()))
		const unknown = Math.round(volume * 0.05 * (0.5 + rng()))
		const spoken = invalid + catchAll + alias + spamTrap + doNotMail + unknown
		return {
			day: p.day,
			counts: {
				valid: Math.max(0, volume - spoken),
				alias,
				catchAll,
				invalid,
				spamTrap,
				doNotMail,
				unknown
			}
		}
	})

	emailCache.set(cacheKey, points)
	return points
}

/** Range totals per outcome key, plus the grand total under "total". */
export const outcomeTotals = (
	points: OutcomePoint[],
	defs: OutcomeDef[]
): Record<string, number> => {
	const sums: Record<string, number> = { total: 0 }
	for (const def of defs) {
		const sum = points.reduce((acc, p) => acc + (p.counts[def.key] ?? 0), 0)
		sums[def.key] = sum
		sums.total += sum
	}
	return sums
}

/**
 * R108: is a RISE in this outcome bad news?
 *
 * The email layer answered this once already. An outcome whose verdict is not
 * `passed` is one we had to flag or stop, so more of it is worse traffic, and
 * reading that table here keeps ONE list instead of two that can disagree. The
 * bot layer has no verdict, so those defs carry the flag themselves.
 *
 * Everything else is neutral, and neutral is the default. The legend paints a
 * neutral row purple and says nothing about good or bad.
 */
export const isWorseWhenUp = (def: OutcomeDef) => {
	// R225: a flag written on the def wins over the verdict, which is how
	// Catch-all stays neutral while its verdict still flags the submission.
	if (def.worseWhenUp !== undefined) return def.worseWhenUp
	const verdict = (def as EmailOutcomeDef).verdict
	if (verdict) return verdict !== 'passed'
	return false
}

/**
 * The same window, one period earlier, per outcome key.
 *
 * `DAY_LABELS` holds 30 calendar days and nothing before them, so a previous
 * 30 days cannot be sliced out of a series: it does not exist in the mock.
 * This draws it instead, from the same seeded generator every other number in
 * this file comes from. So the answer is stable across renders, across a page
 * reload and across a screenshot, and it moves when the range moves.
 *
 * The draw is a multiplier per outcome, applied to the total we really have.
 * The legend then divides two counts. So the percentage it prints is
 * arithmetic on real numbers, not a percentage invented on its own, and the
 * earlier count is here if a tooltip ever wants to name it.
 */
export const previousOutcomeTotals = (
	totals: Record<string, number>,
	defs: OutcomeDef[],
	seed: string
): Record<string, number> => {
	const prev: Record<string, number> = { total: 0 }
	for (const def of defs) {
		// 0.65 to 1.35 of today. A row moves enough to read, and never absurdly.
		const rng = makeRng(hashId(`${seed}/prev/${def.key}`))
		const count = Math.round((totals[def.key] ?? 0) * (0.65 + rng() * 0.7))
		prev[def.key] = count
		prev.total += count
	}
	return prev
}

/* --------------------------- submissions --------------------------- */

const FIRST_OCTETS = [84, 103, 45, 172, 91, 198, 62, 133]

// R316 (Gustavo, 11 Sep): "country actual flag not emoji". Each place carries
// its ISO 3166 alpha-2 code and the DS `Flag` draws the image from it.
export const PLACES: Array<{ location: string; country: string }> = [
	{ location: 'Heritage Pines, US', country: 'US' },
	{ location: 'Austin, US', country: 'US' },
	{ location: 'Toronto, CA', country: 'CA' },
	{ location: 'Manchester, GB', country: 'GB' },
	{ location: 'Berlin, DE', country: 'DE' },
	{ location: 'Guadalajara, MX', country: 'MX' },
	{ location: 'Sydney, AU', country: 'AU' },
	{ location: 'Rotterdam, NL', country: 'NL' },
	{ location: 'Singapore, SG', country: 'SG' },
	{ location: 'São Paulo, BR', country: 'BR' }
]

/** What a page dwell looks like per status. Bots bounce, people read. */
/**
 * The address pools, one per OUTCOME (R104), not per verdict. The address is
 * not decoration: it is the evidence the outcome pill names, so a row that
 * reads `Catch-all` must carry an address at a domain that accepts anything,
 * and a row that reads `Alias` must carry a plus tag. Keyed by outcome, the
 * two can never drift apart.
 *
 * The names are invented. None of these domains is a real customer.
 */
const EMAILS: Record<EmailOutcomeKey, string[]> = {
	/** A person at a mailbox that answers. */
	valid: [
		'dana.whitfield@northpine.co',
		'marcus.leal@brightloom.io',
		'p.okonkwo@harborlane.com',
		'sofia.reyner@meadowcraft.co',
		'j.tanaka@stillwater.dev',
		'amara.boyd@fernwoodgroup.com',
		'lucas.brenner@quaylight.io',
		'nadia.ferreira@oakhollow.co'
	],
	/** A plus tag. It reaches a real person, so we keep it. */
	alias: [
		'k.mercer+trial@brightloom.io',
		'r.singh+aug@harborlane.com',
		'd.whitfield+forms@northpine.co',
		'l.brenner+demo@quaylight.io'
	],
	/** A domain that accepts every address, so delivery proves nothing. */
	catchAll: ['contact@catchall-mailbox.net', 'sales@catchall-mailbox.net', 'hello@openbox-domain.co'],
	/** No such mailbox. Machine output, straight from a bot. */
	invalid: [
		'qz7f2wnd@mailtrap-throwaway.top',
		'x8kd0vlm@mailtrap-throwaway.top',
		'tt41ophr@mailtrap-throwaway.top'
	],
	/** A published trap address. Writing to it costs us the sending domain. */
	spamTrap: ['b3nq9zas@spamsink.click', 'w9vc2mek@spamsink.click'],
	/** A role address that must never receive marketing mail. */
	doNotMail: ['noreply@parked-domain.xyz', 'postmaster@quaylight.io', 'abuse@stillwater.dev'],
	/** The host would not answer, so we could not tell either way. */
	unknown: ['info@disposable-inbox.io', 't.wilder@unreachable-mx.net']
}

const DWELL: Record<
	SubmissionStatus,
	Array<{ timeOnPage: string; interaction: string }>
> = {
	passed: [
		{ timeOnPage: '2m 14s', interaction: 'Normal interaction' },
		{ timeOnPage: '1m 40s', interaction: 'Normal interaction' },
		{ timeOnPage: '3m 05s', interaction: 'Normal interaction' },
		{ timeOnPage: '58s', interaction: 'Normal interaction' }
	],
	flagged: [
		{ timeOnPage: '12s', interaction: 'Low interaction' },
		{ timeOnPage: '9s', interaction: 'Low interaction' },
		{ timeOnPage: '21s', interaction: 'Low interaction' }
	],
	blocked: [
		{ timeOnPage: 'Bounced', interaction: 'No interaction' },
		{ timeOnPage: '1s', interaction: 'No interaction' },
		{ timeOnPage: '2s', interaction: 'No interaction' }
	]
}

const NETWORKS: Record<SubmissionStatus, SubmissionRow['network'][]> = {
	passed: ['Clean', 'Clean', 'Clean', 'VPN'],
	flagged: ['Clean', 'Proxy', 'VPN'],
	blocked: ['Data center', 'Proxy', 'Data center']
}

const submissionsCache = new Map<string, SubmissionRow[]>()

/**
 * The drawer's submissions feed: newest first, one deterministic page of
 * recent traffic per form. Statuses lean the same way the form's lifetime
 * counters do.
 */
export const getSubmissions = (form: FormRecord): SubmissionRow[] => {
	// R150 (audit 2026-08-28): the key holds the toggle, like the two series.
	const cacheKey = `${form.id}:${form.emailValidation ? 'on' : 'off'}`
	const cached = submissionsCache.get(cacheKey)
	if (cached) return cached

	// No submissions, no rows. The feed used to invent 24 of them next to a
	// row that reads "No traffic yet".
	if (form.submissions === 0) {
		submissionsCache.set(cacheKey, [])
		return []
	}

	const rng = makeRng(hashId(`${form.id}/rows`))
	// R104: the outcome draws from its OWN generator. A second call on `rng`
	// would shift every roll after it, and the verdict mix of a form the user
	// already knows would change under a cosmetic edit. Two streams, one seed
	// each, and the feed stays the feed it was.
	const outcomeRng = makeRng(hashId(`${form.id}/outcome`))
	// R295 (Gustavo, 10 Sep, pin #96): the source draws from its own stream
	// too, so the rows a user already knows keep their verdicts and addresses.
	const sourceRng = makeRng(hashId(`${form.id}/source`))
	// The floor is a divide guard for the shares below, nothing more.
	const total = Math.max(form.submissions, 40)
	const blockShare = Math.min(0.55, Math.max(0.06, form.blocked / total))
	const flagShare = Math.min(0.3, Math.max(0.05, form.invalidEmails / total))

	// A descending clock: rows walk back from 09:42 on Aug 18.
	let minutes = 9 * 60 + 42
	let dayIndex = DAY_LABELS.length - 1

	const rows = Array.from({ length: 24 }, (_, i) => {
		minutes -= Math.round(18 + rng() * 150)
		if (minutes < 0) {
			minutes += 24 * 60
			dayIndex = Math.max(0, dayIndex - 1)
		}
		const roll = rng()
		const drawn: SubmissionStatus =
			roll < blockShare ? 'blocked' : roll < blockShare + flagShare ? 'flagged' : 'passed'
		// The verdict picks the outcome, the outcome picks the address. Read in
		// that order the row never contradicts itself: a `Blocked` row cannot
		// come out reading `Valid`, and an `Alias` row always carries a plus tag.
		const pool = OUTCOMES_BY_VERDICT[drawn]
		const rolled = pool[Math.floor(outcomeRng() * pool.length)]
		// R150 (audit 2026-08-28): a form with Email validation off has no
		// verdicts. The feed used to print Invalid and Spam trap pills next to a
		// chart that put every submission in Unknown. The rolls above still run,
		// so a toggle back on returns the same rows the form had.
		const status: SubmissionStatus = form.emailValidation ? drawn : 'passed'
		const outcome: EmailOutcomeKey = form.emailValidation ? rolled : 'unknown'
		const dwell = DWELL[status][Math.floor(rng() * DWELL[status].length)]
		const network = NETWORKS[status][Math.floor(rng() * NETWORKS[status].length)]
		const place = PLACES[Math.floor(rng() * PLACES.length)]
		const email = EMAILS[outcome][Math.floor(rng() * EMAILS[outcome].length)]
		const ip = `${FIRST_OCTETS[Math.floor(rng() * FIRST_OCTETS.length)]}.${Math.floor(
			rng() * 224
		)}.${Math.floor(rng() * 256)}.${Math.floor(rng() * 254) + 1}`
		// Most rows come from Google Ads, a fair share from Meta, fewer from
		// Microsoft, and some from elsewhere: 55 / 25 / 10 / 10.
		const sourceRoll = sourceRng()
		const source: TrafficSource =
			sourceRoll < 0.55
				? 'Google Ads'
				: sourceRoll < 0.8
				? 'Meta Ads'
				: sourceRoll < 0.9
				? 'Microsoft Ads'
				: 'Other'
		return {
			id: `${form.id}-row-${i}`,
			formId: form.id,
			day: DAY_LABELS[dayIndex],
			clock: `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(
				minutes % 60
			).padStart(2, '0')}`,
			status,
			outcome,
			email,
			ip,
			timeOnPage: dwell.timeOnPage,
			interaction: dwell.interaction,
			network,
			location: place.location,
			country: place.country,
			source
		}
	})

	// A filter needs something to narrow. If one tape lands every row on the
	// same network, the second row moves to the next one in display order.
	if (rows.length > 4 && rows.every((row) => row.source === rows[0].source)) {
		const at = TRAFFIC_SOURCES.indexOf(rows[0].source)
		rows[1].source = TRAFFIC_SOURCES[(at + 1) % TRAFFIC_SOURCES.length]
	}

	submissionsCache.set(cacheKey, rows)
	return rows
}

/** The instant a row came in, for the newest-first order and the Date sort. */
const submissionInstant = (row: SubmissionRow) =>
	Date.parse(`${row.day} 2026 ${row.clock}`)

/**
 * Every form's submissions in one list, newest first. R298 (Gustavo, 11 Sep,
 * pin #100): this feeds the page's Submissions tab, one table across all
 * forms. `getSubmissions` is cached per form, so the flatten costs one sort.
 * The sort is stable, so two rows on the same minute keep their form order.
 */
export const getAllSubmissions = (forms: FormRecord[]): SubmissionRow[] =>
	forms
		.flatMap((form) => getSubmissions(form))
		.sort((a, b) => submissionInstant(b) - submissionInstant(a))

/**
 * The coarse verdict as a pill. R104 moved the submissions table to the finer
 * outcome pill, so nothing reads this today. It stays because the three feed
 * filters still speak this vocabulary, and a surface that shows the verdict
 * alone must not hand-roll the mapping again.
 */
export const SUBMISSION_META: Record<
	SubmissionStatus,
	{ label: string; color: 'success' | 'warning' | 'error' }
> = {
	passed: { label: 'Passed', color: 'success' },
	flagged: { label: 'Flagged', color: 'warning' },
	blocked: { label: 'Blocked', color: 'error' }
}

/**
 * Deterministic "Linked" label. Always earlier than any lastSeen mock.
 *
 * R106: the word is `Linked`, not `Created`. ClickGuard never creates the
 * form. The customer built it, and this date is the day they pointed us at
 * it, which is the only date we can honestly know. The list header, the
 * wizard and the empty state all say "link a form", so the drawer says it too.
 */
export const getLinkedLabel = (form: FormRecord) =>
	DAY_LABELS[hashId(form.id) % 12]

/**
 * The field the CUSTOMER creates in their own CRM.
 *
 * Ralph Perrier, 2026-08-20 product sync: "CRMs are a minefield... that's a
 * liability we don't really want to step into." So ClickGuard never creates a
 * field, never renames one and never writes a schema. We name the field, the
 * CRM owner creates it, and then we look for it and report what we found.
 *
 * Brevo stores attribute names in upper case (FIRSTNAME, LASTNAME), so its
 * name is upper case too. HubSpot takes a plain label and derives its own
 * internal name from it.
 */
export interface CrmFieldSpec {
	/** The name the customer must type into their CRM. */
	name: string
	/** The field type the customer must pick. */
	type: string
	/** The object, or the object picker value, the field belongs to. */
	object: string
	/** The values ClickGuard writes into it. */
	holds: string
	/** The menu path to the place where the CRM adds a field. */
	where: string
}

/**
 * R124 (Gustavo, pin hqfow3): each CRM gets its own recipe, in its own words.
 * HubSpot calls the thing a property, keeps it under Preferences > Data
 * Management, and picks the object from a dropdown that reads "Contact
 * properties". A Salesforce recipe sent a HubSpot admin hunting.
 *
 * The values we write are the same in both. R306 (Gustavo, pin #84,
 * 2026-09-11, Esteban's go): the seven email verdicts, the `EMAIL_OUTCOMES`
 * labels in lower case, in his order (valid, invalid, spam trap) then the
 * rest. The CRM card on screen and the instruction sheet both print this
 * phrase, so keep it in step with those labels. His list names no bot value,
 * so a bot verdict has no place in the field yet; that stays his call.
 */
export const CRM_FIELDS: Record<'brevo' | 'hubspot', CrmFieldSpec> = {
	brevo: {
		name: 'CLICKGUARD_STATUS',
		type: 'Text',
		object: 'Normal attributes',
		holds: 'valid, invalid, spam trap, do not mail, catch-all, alias or unknown',
		where: 'Settings > Contacts > Contact attributes'
	},
	hubspot: {
		name: 'Deliverability (ClickGuard)',
		type: 'Single-line text',
		object: 'Contact properties',
		holds: 'valid, invalid, spam trap, do not mail, catch-all, alias or unknown',
		where: 'Settings > Preferences > Data Management > Properties'
	}
}

/** The Brevo recipe, for a reader that has no provider yet. */
export const CRM_FIELD: CrmFieldSpec = CRM_FIELDS.brevo

/**
 * How long the dashboard keeps a copy of a validated email address.
 *
 * Ralph, same sync: the dashboard records a copy of every validated address,
 * and that needs a disclosure and a rule the customer sets. Fraud Blocker does
 * almost the same thing today. After the period ends we delete the address and
 * keep the count only, so the charts still work with no address on file.
 */
export type RetentionKey = '30d' | '90d' | '12m'

export interface RetentionDef {
	key: RetentionKey
	label: string
	/** The sentence under the control, in the customer's words. */
	desc: string
}

export const RETENTIONS: RetentionDef[] = [
	{
		key: '30d',
		label: '30 days',
		desc: 'We delete each address 30 days after the form receives it.'
	},
	{
		key: '90d',
		label: '90 days',
		desc: 'We delete each address 90 days after the form receives it.'
	},
	{
		key: '12m',
		label: '12 months',
		desc: 'We delete each address 12 months after the form receives it.'
	}
]

export const DEFAULT_RETENTION: RetentionKey = '90d'
